import React, { useState } from 'react';
import {
  ISchemaDataModelFieldsFragment,
  ITableColumnFieldsFragment,
  JoinConditionOperator,
} from '../../../../utils/apollo/graphqlSchema';
import {
  emptyTemporalCondition,
  IEditJoinModel,
  JoinTypeOption,
} from './utils';
import DataModelerEditJoinPair from './DataModelerEditJoinPair';
import DataModelerEditTemporalCondition from './DataModelerEditTemporalCondition';
import DataModelerEditExistingJoinPair from './DataModelerEditExistingJoinPair';
import { JoinConditionRow } from './DataModelerJoinUI';
import FieldLabel from '../../../../components/Form/FieldLabel';
import DataModelerEditJoinSqlCondition from './DataModelerEditJoinSqlCondition';
import { useDataModelerEditSeFeature } from '../../../../utils/hooks';

interface IProps {
  isEditMode: boolean;
  joinModel: IEditJoinModel;
  schemaObj: ISchemaDataModelFieldsFragment;
  viewKey: string;
  tableKey: string;
  availableColumnsFromServer: ITableColumnFieldsFragment[];
}

const DataModelerEditJoin = ({
  isEditMode,
  joinModel,
  schemaObj,
  viewKey,
  tableKey,
  availableColumnsFromServer,
}: IProps) => {
  const {
    joinData,
    addCondition,
    removeCondition,
    updateCondition,
    setTemporalCondition,
    setSqlCondition,
  } = joinModel;

  const editSeFeatureEnablked = useDataModelerEditSeFeature();
  const isAdvancedEditMode = isEditMode && editSeFeatureEnablked;
  const hasSqlConditionRow = isAdvancedEditMode || !!joinData.sqlCondition;

  const [draftSelection, setDraftSelection] = useState<{
    factFieldKey?: string;
    dimTableColumn?: string;
    relOp: JoinConditionOperator;
  } | null>(null);

  const [draftIsSubject, setDraftIsSubject] = useState<boolean>(
    joinData.conditions.length === 0,
  );

  const draftRelOp = draftSelection?.relOp ?? JoinConditionOperator.Equal;
  const handleSetDraftRelOp = (relOp: JoinConditionOperator) => {
    setDraftSelection({
      factFieldKey: draftSelection?.factFieldKey,
      dimTableColumn: draftSelection?.dimTableColumn,
      relOp,
    });
  };

  const handleSetDraftFacFieldKey = (key: string, isSubject: boolean) => {
    if (draftSelection && draftSelection.dimTableColumn) {
      addCondition(
        key,
        draftSelection.dimTableColumn,
        draftSelection.relOp,
        isSubject,
      );
      setDraftSelection(null);
    } else {
      setDraftSelection({ factFieldKey: key, relOp: draftRelOp });
      setDraftIsSubject(isSubject);
    }
  };

  const handleSetDraftDimTableColumn = (column: string, isSubject: boolean) => {
    if (draftSelection && draftSelection.factFieldKey) {
      addCondition(
        draftSelection.factFieldKey,
        column,
        draftSelection.relOp,
        isSubject,
      );
      setDraftSelection(null);
    } else {
      setDraftSelection({ dimTableColumn: column, relOp: draftRelOp });
    }
  };

  const switchToTemporalCondition =
    !!joinData.temporalCondition || joinData.conditions.length === 0
      ? null
      : () => {
          setTemporalCondition(emptyTemporalCondition());
        };

  const setDraftJoinOptionFromTemporal = (newOption: JoinTypeOption) => {
    if (newOption === 'Temporal') {
      return;
    }
    setDraftIsSubject(newOption === 'Subject');
  };

  const draftRemove = !!draftSelection
    ? () => {
        setDraftSelection(null);
      }
    : undefined;

  const handleSetDraftIsSubject = (isSubject: boolean) => {
    if (isSubject !== draftIsSubject) {
      if (draftSelection !== null) {
        // reset the fact field key if the isSubject status changed
        if (draftSelection.dimTableColumn) {
          setDraftSelection({
            dimTableColumn: draftSelection.dimTableColumn,
            relOp: draftSelection.relOp,
          });
        } else {
          setDraftSelection(null);
        }
      }
    }
    setDraftIsSubject(isSubject);
  };

  return (
    <>
      <JoinConditionRow $isHeader={true}>
        <div>
          <FieldLabel>Type</FieldLabel>
        </div>
        <div>
          <FieldLabel>Fact Field</FieldLabel>
        </div>
        <div>
          <FieldLabel>Operator</FieldLabel>
        </div>
        <div>
          <FieldLabel>Dimension Column(s)</FieldLabel>
        </div>
      </JoinConditionRow>
      {joinData.conditions.map((joinPair, ix) => {
        const setFactFieldKey = (s: string) => {
          updateCondition(ix, s, joinPair.dimTableColumn, joinPair.relOp);
        };
        const setDimTableColumn = (s: string) => {
          updateCondition(ix, joinPair.factFieldKey, s, joinPair.relOp);
        };
        const setRelOp = (newOp: JoinConditionOperator) => {
          updateCondition(
            ix,
            joinPair.factFieldKey,
            joinPair.dimTableColumn,
            newOp,
          );
        };
        const removeThisCondition = () => removeCondition(ix);
        const isLast =
          ix === joinData.conditions.length - 1 &&
          !joinData.temporalCondition &&
          !isEditMode &&
          !hasSqlConditionRow;
        return (
          <DataModelerEditExistingJoinPair
            isLast={isLast}
            isEditMode={isEditMode}
            key={`${joinPair.factFieldKey}-${ix}`}
            isSubjectInitial={joinPair.isSubject}
            switchToTemporal={switchToTemporalCondition}
            factViewKey={schemaObj.fact.key}
            factFieldKey={joinPair.factFieldKey}
            setFactFieldKey={setFactFieldKey}
            relOp={joinPair.relOp}
            setRelOp={setRelOp}
            dimViewKey={viewKey}
            dimTableKey={tableKey}
            dimTableColumn={joinPair.dimTableColumn}
            setDimTableColumn={setDimTableColumn}
            availableColumnsFromServer={availableColumnsFromServer}
            remove={removeThisCondition}
          />
        );
      })}
      {joinData.temporalCondition && (
        <DataModelerEditTemporalCondition
          isLast={!isEditMode && !hasSqlConditionRow}
          isEditMode={isEditMode}
          factViewKey={schemaObj.fact.key}
          viewKey={viewKey}
          tableKey={tableKey}
          availableColumnsFromServer={availableColumnsFromServer}
          temporalCondition={joinData.temporalCondition}
          setTemporalCondition={setTemporalCondition}
          setDraftJoinOption={setDraftJoinOptionFromTemporal}
        />
      )}
      {isEditMode && (
        <DataModelerEditJoinPair
          isLast={!hasSqlConditionRow}
          isEditMode={isEditMode}
          key={`nc-${joinData.conditions.length}-${draftSelection?.factFieldKey}-${draftSelection?.dimTableColumn}`}
          isSubject={draftIsSubject}
          setIsSubject={handleSetDraftIsSubject}
          switchToTemporal={switchToTemporalCondition}
          factViewKey={schemaObj.fact.key}
          factFieldKey={draftSelection?.factFieldKey}
          setFactFieldKey={handleSetDraftFacFieldKey}
          setRelOp={handleSetDraftRelOp}
          relOp={draftRelOp}
          dimViewKey={viewKey}
          dimTableKey={tableKey}
          dimTableColumn={draftSelection?.dimTableColumn}
          setDimTableColumn={handleSetDraftDimTableColumn}
          availableColumnsFromServer={availableColumnsFromServer}
          remove={draftRemove}
        />
      )}
      {hasSqlConditionRow && (
        <DataModelerEditJoinSqlCondition
          isAdvancedEditMode={isAdvancedEditMode}
          sqlCondition={joinData.sqlCondition}
          setSqlCondition={setSqlCondition}
        />
      )}
    </>
  );
};

export default DataModelerEditJoin;
